<template>
  <section class="white-model-workspace">
    <div class="white-model-main">
      <aside class="white-model-settings">
        <section class="setting-section">
          <header class="section-title">
            <span>01</span>
            <div><strong>上传原始效果图</strong><small>建议使用构图完整、细节清晰的 PNG / JPG / WebP</small></div>
          </header>

          <label
            class="plan-dropzone"
            :class="{ filled: previewUrl, dragging }"
            @dragenter.prevent="dragging = true"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="handleDrop"
          >
            <input type="file" accept="image/png,image/jpeg,image/webp" @change="handleFile" />
            <img v-if="previewUrl" :src="$ossImageUrl(previewUrl, 'preview')" alt="待转换的原始室内效果图" decoding="async" />
            <span v-else class="dropzone-empty">
              <i class="el-icon-upload2"></i>
              <strong>{{ dragging ? '松开即可上传' : '选择室内效果图' }}</strong>
              <small>点击选择或拖入图片，上传后将严格保留原图构图、透视和物体位置</small>
            </span>
            <button v-if="previewUrl" type="button" class="replace-plan">更换</button>
          </label>

          <div class="file-rule" :class="{ valid: previewUrl }">
            <i :class="previewUrl ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
            <span>{{ previewUrl ? (floorPlanFile ? floorPlanFile.name : '历史任务原图') : '请上传单张完整室内效果图，不要上传拼图或平面图' }}</span>
          </div>
          <div v-if="sourceImage.width" class="image-detection">
            <span><strong>{{ sourceImage.width }} × {{ sourceImage.height }}</strong>{{ sourceImage.orientationLabel }}</span>
            <span>原图比例约 {{ sourceImage.ratioLabel }}</span>
          </div>
        </section>

        <section class="setting-section dimension-section">
          <header class="section-title">
            <span>02</span>
            <div><strong>设置线稿表现</strong><small>默认只保留灯具点位，不生成光晕和光束</small></div>
          </header>
          <div class="output-row">
            <span>线条表现</span>
            <el-select v-model="lineWeight" size="small" aria-label="选择线稿线条表现">
              <el-option label="精细白线 · 细节丰富" value="精细白线" />
              <el-option label="标准蜡笔线 · 推荐" value="标准蜡笔线" />
              <el-option label="粗犷蜡笔线 · 手绘感强" value="粗犷蜡笔线" />
            </el-select>
          </div>
          <div class="output-row lighting-outline-row">
            <span>灯具表现</span>
            <el-radio-group v-model="lightingMode" size="small" aria-label="选择灯具在线稿中的表现">
              <el-radio-button label="仅点位">仅点位</el-radio-button>
              <el-radio-button label="极简轮廓">极简轮廓</el-radio-button>
            </el-radio-group>
          </div>
          <label class="note-field">
            <span>补充要求 <em>{{ lineNote.length }}/300</em></span>
            <textarea
              v-model="lineNote"
              maxlength="300"
              placeholder="例如：准确保留天花筒灯、线性灯带和吊灯的安装点位"
            ></textarea>
            <AiPromptOptimizer
              v-model="lineNote"
              :disabled="generating"
              :max-length="300"
              context="室内效果图转换白模的补充要求"
            />
          </label>
        </section>

        <section class="setting-section output-section">
          <header class="section-title">
            <span>03</span>
            <div><strong>线稿输出</strong><small>上传后自动匹配最接近的模型画幅，避免构图被裁切</small></div>
          </header>
          <div class="output-row">
            <span>画幅</span>
            <AspectRatioSelector
              v-model="selectedRatio"
              compact
              :disabled="generating"
              :options="ratioOptions"
              aria-label="选择线稿输出画幅"
              @change="ratioMode = 'manual'"
            />
          </div>
          <div v-if="sourceImage.width" class="ratio-match-note">
            <i class="el-icon-magic-stick"></i>
            <span v-if="ratioMode === 'auto'">已根据原图自动匹配 {{ selectedRatio }}，生成时将完整保留画面内容</span>
            <span v-else>已手动选择 {{ selectedRatio }}</span>
            <button v-if="ratioMode === 'manual'" type="button" @click="useDetectedRatio">跟随原图</button>
          </div>
          <div class="output-row">
            <span>分辨率</span>
            <ResolutionSelector v-model="selectedResolution" compact :disabled="generating" :show-label="false" />
          </div>
          <div class="output-row">
            <span>生成质量</span>
            <QualitySelector v-model="selectedQuality" compact :disabled="generating" />
          </div>
        </section>

        <button class="generate-white-model" :disabled="generating" @click="generateWhiteModel">
          <span v-if="generating" class="button-spinner"></span>
          <i v-else class="el-icon-edit-outline"></i>
          <span>{{ generating ? generationText : '生成黑底白线稿' }}</span>
          <small v-if="!generating">生成 1 张线稿方案</small>
        </button>
        <div v-if="generating" class="generation-progress"><i :style="{ width: `${progress}%` }"></i></div>
      </aside>

      <section class="comparison-panel">
        <header class="comparison-head">
          <div>
            <strong>原始效果图与线稿图对照</strong>
            <span>生成后重点核对构图、透视、空间结构、软装位置和灯具点位</span>
          </div>
          <div class="comparison-actions">
            <WorkflowTuningButton
              v-if="resultUrl"
              variant="button"
              icon-only
              :image-url="resultUrl"
              :prompt="finalPrompt"
              source-model="gpt-image-2-official"
              module-name="黑底线稿"
            />
            <button
              v-if="resultUrl"
              title="下载线稿图"
              aria-label="下载线稿图"
              @click="downloadResult"
            ><i class="el-icon-download"></i></button>
            <button
              v-if="resultUrl"
              title="重新生成"
              aria-label="重新生成"
              :disabled="generating"
              @click="generateWhiteModel"
            ><i class="el-icon-refresh"></i></button>
          </div>
        </header>

        <div class="comparison-stage" :class="{ 'has-result': resultUrl }">
          <article class="compare-viewport source-viewport" :class="{ zoomable: previewUrl }" :tabindex="previewUrl ? 0 : null" :role="previewUrl ? 'button' : null" aria-label="查看原始效果图大图" @click="previewUrl && previewCurrentImage('source')" @keydown.enter.prevent="previewUrl && previewCurrentImage('source')">
            <span class="viewport-label">A · 原始效果图</span>
            <img v-if="previewUrl" :src="$ossImageUrl(previewUrl, 'preview')" alt="原始室内效果图" decoding="async" />
            <div v-else class="viewport-empty">
              <i class="el-icon-picture-outline"></i>
              <strong>等待效果图</strong>
              <span>上传后在这里检查原始构图与细节</span>
            </div>
          </article>
          <article class="compare-viewport model-viewport" :class="{ zoomable: resultUrl }" :tabindex="resultUrl ? 0 : null" :role="resultUrl ? 'button' : null" aria-label="查看黑底线稿大图" @click="resultUrl && previewCurrentImage('result')" @keydown.enter.prevent="resultUrl && previewCurrentImage('result')">
            <span class="viewport-label">B · 黑底白线稿</span>
            <img v-if="resultUrl" :src="$ossImageUrl(resultUrl, 'preview')" alt="AI生成的黑底白线蜡笔手绘线稿" decoding="async" />
            <div v-else-if="generating" class="viewport-empty model-loading">
              <span class="model-loader"><i></i><i></i><i></i></span>
              <strong>{{ generationText }}</strong>
              <span>正在保持原图构图并提取空间轮廓与灯具点位</span>
            </div>
            <div v-else class="viewport-empty white-model-empty">
              <i class="el-icon-edit-outline line-art-empty-icon"></i>
              <strong>等待生成线稿</strong>
              <span>黑底白线，软装仅保留轮廓，灯具只显示安装点位</span>
            </div>
          </article>
        </div>

        <div class="accuracy-strip">
          <span><i class="el-icon-check"></i> 构图与透视不变</span>
          <span><i class="el-icon-check"></i> 空间结构不变</span>
          <span><i class="el-icon-check"></i> 软装只显示轮廓</span>
          <span><i class="el-icon-check"></i> 灯具只保留点位</span>
          <span><i class="el-icon-check"></i> 黑底白线无彩色填充</span>
        </div>

        <div v-if="errorMessage" class="generation-error">
          <i class="el-icon-warning-outline"></i>
          <div>
            <strong>{{ errorMessage }}</strong>
            <span>{{ errorSuggestion }}</span>
          </div>
          <button v-if="currentTaskIds.length && errorRetryable" :disabled="generating" @click="continuePolling">
            继续查询
          </button>
        </div>
      </section>
    </div>

    <section class="ai-method-panel">
      <header class="method-head">
        <div>
          <span class="workspace-kicker">AI METHOD & PROMPT</span>
          <h2>AI 是怎样把效果图转换成线稿的</h2>
        </div>
        <button class="prompt-toggle" @click="promptVisible = !promptVisible">
          <i :class="promptVisible ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          {{ promptVisible ? '收起提示词' : '查看对应提示词' }}
        </button>
      </header>

      <div class="method-steps">
        <article v-for="item in methodSteps" :key="item.number">
          <span>{{ item.number }}</span>
          <i :class="item.icon"></i>
          <div><strong>{{ item.title }}</strong><p>{{ item.description }}</p></div>
        </article>
      </div>

      <transition name="prompt-reveal">
        <div v-if="promptVisible" class="prompt-content">
          <section class="prompt-block system-prompt-block">
            <header>
              <div><span>SYSTEM PROMPT</span><strong>线稿转换系统提示词</strong></div>
              <button title="复制系统提示词" aria-label="复制系统提示词" @click="copyText(systemPrompt)">
                <i class="el-icon-copy-document"></i>
              </button>
            </header>
            <pre>{{ systemPrompt }}</pre>
          </section>

          <section class="prompt-block">
            <header>
              <div><span>FINAL PROMPT</span><strong>本次提交给图片模型的提示词</strong></div>
              <button title="复制最终提示词" aria-label="复制最终提示词" @click="copyText(finalPrompt)">
                <i class="el-icon-copy-document"></i>
              </button>
            </header>
            <pre>{{ finalPrompt }}</pre>
          </section>

          <section class="negative-constraints">
            <strong>负向约束</strong>
            <span v-for="item in negativeConstraints" :key="item">{{ item }}</span>
          </section>
        </div>
      </transition>
    </section>

    <section class="white-model-history">
      <header class="history-head">
        <div>
          <span class="workspace-kicker">GENERATION RECORDS</span>
          <h2>线稿生成记录</h2>
          <p>展示全部线稿任务，点击记录可恢复查看原始效果图与生成结果。</p>
        </div>
        <button type="button" class="history-refresh" :disabled="historyLoading" @click="loadPendingTask">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>
          {{ historyLoading ? '正在刷新' : '刷新记录' }}
        </button>
      </header>

      <div v-if="history.length" class="white-model-history-grid">
        <article v-for="record in history" :key="record.id" :class="{ active: currentBatchId === record.id }">
          <div class="history-preview">
            <span class="history-status" :class="record.status">{{ statusText(record.status) }}</span>
            <button
              v-if="record.source"
              type="button"
              class="history-image"
              title="点击放大历史原始效果图"
              aria-label="放大历史原始效果图"
              @click="previewHistoryImage(record, 'source')"
            >
              <img
                :src="$ossImageUrl(record.source, 'thumbnail')"
                alt="历史线稿原始效果图"
                loading="lazy"
                decoding="async"
              />
              <small>原始效果图</small>
              <i class="el-icon-zoom-in history-zoom-icon"></i>
            </button>
            <div v-else class="history-image">
              <span><i class="el-icon-picture-outline"></i>原图不可用</span>
              <small>原始效果图</small>
            </div>
            <button
              v-if="record.result"
              type="button"
              class="history-image result"
              title="点击放大历史线稿结果"
              aria-label="放大历史线稿结果"
              @click="previewHistoryImage(record, 'result')"
            >
              <img
                :src="$ossImageUrl(record.result, 'thumbnail')"
                alt="历史黑底白线稿结果"
                loading="lazy"
                decoding="async"
              />
              <small>黑底白线稿</small>
              <i class="el-icon-zoom-in history-zoom-icon"></i>
            </button>
            <div v-else class="history-image result">
              <span>
                <i :class="record.status === 'generating' ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>
                {{ statusText(record.status) }}
              </span>
              <small>黑底白线稿</small>
            </div>
          </div>
          <div class="history-image-dimension">
            <i class="el-icon-full-screen"></i>
            <span>图片尺寸</span>
            <strong>{{ historyImageDimensions(record) }}</strong>
          </div>
          <GenerationModelDetails :record="record" />
          <GenerationFailureDetails :record="record" />
          <footer>
            <div>
              <strong>{{ record.style || '黑底白线蜡笔线稿' }}</strong>
              <dl class="history-record-details">
                <div>
                  <dt>图片比例</dt>
                  <dd>{{ record.size || '16:9' }}</dd>
                </div>
                <div>
                  <dt>生成时间</dt>
                  <dd><time class="creation-date" :datetime="record.createdAt || undefined">{{ formatRecordTime(record.createdAt) }}</time></dd>
                </div>
              </dl>
            </div>
            <div class="history-record-actions">
              <WorkflowTuningButton
                v-if="record.result"
                variant="button"
                icon-only
                :image-url="record.result"
                :prompt="record.prompt || finalPrompt"
                :source-model="record.model || ''"
                module-name="黑底线稿"
              />
              <button
                type="button"
                title="恢复查看这条记录"
                aria-label="恢复查看这条记录"
                @click="restoreHistoryRecord(record)"
              ><i class="el-icon-view"></i></button>
              <button
                v-if="record.result"
                type="button"
                title="下载这张线稿图"
                aria-label="下载这张线稿图"
                @click="downloadHistoryResult(record)"
              ><i class="el-icon-download"></i></button>
            </div>
          </footer>
        </article>
      </div>

      <div v-else class="history-empty">
        <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i>
        <strong>{{ historyLoading ? '正在加载生成记录' : '还没有线稿生成记录' }}</strong>
        <span>{{ historyError || '完成第一次线稿生成后，原图和结果图会自动显示在这里。' }}</span>
      </div>
      <AppPagination :page="historyPage" :page-size="12" :total="historyTotal" :loading="historyLoading" @change="changeHistoryPage" />
    </section>
  </section>
</template>

<script>
/* 白模/线稿类工具以原图为唯一结构依据，前端只负责上传、参数和结果展示。 */
import { apiRequest } from '@/services/api'
import { downloadGeneratedImageDirectly, downloadImageAsPng } from '@/utils/imageDownload'
import AspectRatioSelector from '@/components/AspectRatioSelector.vue'
import ResolutionSelector from '@/components/ResolutionSelector.vue'
import QualitySelector from '@/components/QualitySelector.vue'
import AiPromptOptimizer from '@/components/AiPromptOptimizer.vue'
import { mergePendingGenerations, removePendingGeneration, savePendingGeneration } from '@/utils/pendingGenerationRecords'
import { DEFAULT_GENERATION_QUALITY, MINIMUM_OUTPUT_RESOLUTION } from '@/utils/outputQuality'
import { closestImageRatio, SUPPORTED_IMAGE_RATIOS } from '@/utils/imageAspectRatio'

const FALLBACK_SYSTEM_PROMPT = `你是资深室内建筑线稿艺术家和图像重绘控制专家。你的唯一任务，是把用户上传的单张室内效果图转换为“纯黑背景、白色蜡笔线条”的专业设计线稿，同时严格保持原图内容和空间关系不变。

强制规则：
1. 原效果图是唯一依据。保持原图画幅、裁切范围、镜头机位、透视、构图、墙顶地边界、门窗、柜体、家具、灯具、饰品、植物及所有物体的数量、位置、比例和朝向一致；只改变视觉表现，不做重新设计。
2. 输出必须是严格无彩色的黑白二值线稿：背景统一为纯黑（#000000），所有可见线条统一为中性纯白（#FFFFFF）。仅轮廓抗锯齿边缘允许出现 R=G=B 的中性灰像素；任何可见元素都不得带色相或饱和度。严禁米黄、暖白、奶油色、棕色、金色、橙色、蓝色等彩色线条，严禁继承原图材质颜色。
3. 建筑硬装以清晰连续的轮廓线表达；家具、布艺、摆件、植物等软装只保留外轮廓和极少必要结构线，不填充体积阴影，不描绘真实材质纹理。面对编织、木纹、石纹、布纹、格栅、植物等复杂区域，必须主动简化为稀疏的主要轮廓和必要结构线，宁可省略次要纹理，也不得生成密集交叉线、照片质感或带颜色的微小线条。
4. 准确保留原图中筒灯、射灯、灯带、吊灯等灯具的位置、数量和方向。默认只画灯具自身的点位或极简外轮廓：筒灯和射灯用小型白色点位/灯孔轮廓表示，灯带用连续细白线表示；不绘制锥形光束、弧形照明边界、光斑、溢光、反射光、发光雾或任何光晕。
5. 地面、墙面和柜门仅保留必要透视线、分缝线与转折线；减少重复细纹，避免线条堆叠成白块。画面须有清晰的黑色留白和层级。
6. 禁止新增、删除、移动、替换或美化任何结构、家具、灯具和软装；禁止改变装修方案、颜色方案、昼夜环境或镜头；禁止输出文字、标注、尺寸、边框、水印、拼图或分析过程。
7. 无法确认的细节用最少线条保守表达，不得臆造。内容一致性和轮廓可读性高于艺术夸张。输出前必须检查整张图：若存在任何有色像素、暖色线条或残留材质颜色，先彻底去色并改为纯白线条；背景必须保持均匀纯黑。只输出一张最终线稿图。`

export default {
  name: 'WhiteModelCreation',
  components: { AspectRatioSelector, ResolutionSelector, QualitySelector, AiPromptOptimizer },
  data() {
    return {
      floorPlanFile: null,
      previewUrl: '',
      dragging: false,
      sourceImage: {
        width: 0,
        height: 0,
        ratio: 0,
        ratioLabel: '',
        orientation: '',
        orientationLabel: ''
      },
      ratioMode: 'auto',
      lineWeight: '标准蜡笔线',
      lightingMode: '仅点位',
      lineNote: '',
      selectedRatio: '16:9',
      ratioOptions: SUPPORTED_IMAGE_RATIOS,
      selectedResolution: MINIMUM_OUTPUT_RESOLUTION,
      selectedQuality: DEFAULT_GENERATION_QUALITY,
      apiConfigured: false,
      generating: false,
      progress: 0,
      generationText: '准备上传效果图',
      resultUrl: '',
      errorMessage: '',
      errorSuggestion: '',
      errorRetryable: false,
      currentBatchId: '',
      currentTaskIds: [],
      promptVisible: true,
      systemPrompt: FALLBACK_SYSTEM_PROMPT,
      requestControllers: [],
      pollingStopped: false,
      pendingRecordTimer: null,
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyTotal: 0,
      historyError: '',
      methodSteps: [
        { number: '01', icon: 'el-icon-picture-outline', title: '锁定原图内容', description: '识别原图构图、透视、硬装、家具、软装和灯具位置，建立不可改动清单。' },
        { number: '02', icon: 'el-icon-edit-outline', title: '提取空间轮廓', description: '以白色蜡笔线提取硬装主轮廓，控制线条密度并保留黑色留白。' },
        { number: '03', icon: 'el-icon-sunny', title: '保留灯具点位', description: '准确保留筒灯、射灯、灯带和吊灯的位置，不绘制光束、光斑或光晕。' },
        { number: '04', icon: 'el-icon-view', title: '核对线稿一致性', description: '检查结构、家具和软装没有增删移位，只输出最终黑底白线稿。' }
      ],
      negativeConstraints: [
        '不改构图', '不改透视', '不改结构', '不增减家具',
        '软装不填充', '灯具强制不发光', '灯具无光斑光晕光束', '不输出彩色', '不生成写实材质', '不使用大面积灰阶', '不输出文字水印'
      ]
    }
  },
  computed: {
    currentViewerItems() {
      return [
        this.previewUrl ? { src: this.previewUrl, title: '黑底线稿 · 原始效果图', key: 'current-source' } : null,
        this.resultUrl ? { src: this.resultUrl, title: '黑底线稿 · 生成结果', key: 'current-result' } : null
      ].filter(Boolean)
    },
    historyViewerItems() {
      return this.history.reduce((items, record) => {
        if (record.source) items.push({ src: record.source, title: '黑底线稿记录 · 原始效果图', key: `${record.id}-source` })
        if (record.result) items.push({ src: record.result, title: '黑底线稿记录 · 生成结果', key: `${record.id}-result` })
        return items
      }, [])
    },
    stylePrompt() {
      const lighting = this.lightingMode === '仅点位'
        ? '只保留筒灯、射灯、灯带和吊灯自身的准确安装点位，不画任何光束、照明范围或光晕'
        : '以极简白线保留灯具自身外轮廓，但不画光束、照明范围、光斑、溢光或光晕'
      return `线条表现：${this.lineWeight}；灯光表现：${lighting}`
    },
    aspectRatioPrompt() {
      if (!this.sourceImage.width) {
        return `输出画幅使用 ${this.selectedRatio}，不得拉伸画面主体。`
      }
      const adaptation = this.sourceImage.ratioLabel === this.selectedRatio
        ? '输出画幅与原图比例一致'
        : `模型画幅使用最接近的 ${this.selectedRatio}；比例差异只能通过延展纯黑背景或增加留白适配`
      return `原图尺寸为 ${this.sourceImage.width}×${this.sourceImage.height}，原图比例约 ${this.sourceImage.ratioLabel}，方向为${this.sourceImage.orientationLabel}。${adaptation}。严禁拉伸、压缩或裁掉原图内容，主体在画布中的相对尺寸、位置、构图和透视必须保持不变。`
    },
    finalPrompt() {
      const note = this.lineNote.trim() || '无额外要求，严格按原效果图转换'
      return `【强制硬性规则（最高优先级，不可违反）】所有生成的黑白线稿中，灯具一律只允许显示实体轮廓、灯孔或安装点位；严禁出现任何光斑、光晕、光束、投射光、照明范围、溢光、反射光、发光雾、亮度渐变或受光阴影。无论原图、灯具类型、用户补充要求或其他提示词如何描述，都必须执行“灯具完全不发光、绝不出现光斑”。
【室内效果图转黑底白线蜡笔线稿】
原效果图是唯一内容依据。保持画幅、裁切、镜头机位、透视、构图、空间结构、门窗、柜体、家具、灯具、饰品、植物和软装的数量、位置、比例、朝向完全不变，只转换视觉表现。
画幅适配：${this.aspectRatioPrompt}
线稿要求：必须输出严格无彩色的黑白二值线稿——背景统一为纯黑（#000000），所有可见线条统一为中性纯白（#FFFFFF）；仅抗锯齿边缘允许 R=G=B 的中性灰，任何区域不得带色相或饱和度。严禁米黄、暖白、奶油色、棕色、金色、橙色、蓝色线条，绝不继承原图的材质颜色。${this.stylePrompt}。硬装使用清晰主轮廓和少量必要分缝；家具、布艺、摆件、植物等软装只显示外轮廓及极少结构线，不填充阴影，不表现真实材质。编织、木纹、石纹、布纹、格栅、植物等复杂区域必须简化成稀疏主轮廓，宁可省略次要纹理，也不得生成密集交叉线、照片纹理或带颜色的细线。灯具只表达自身点位或极简外轮廓：筒灯、射灯以小型白色点位/灯孔表示，灯带以连续细白线表示。不得生成锥形光束、弧形照明边界、照明范围、光斑、溢光、反射光、发光雾或任何光晕。
补充要求：${note}。
禁止重新设计，禁止新增、删除、移动、替换或美化任何内容；禁止改变昼夜环境或镜头；禁止彩色填充、照片纹理、写实材质、大面积灰阶、文字、尺寸、边框、水印、拼图。减少无意义细纹和过度描边，保持黑色留白、线条层级和专业设计草图质感。输出前检查整图并彻底去色：发现任何有色像素、暖色线条或残留材质颜色时，必须改成中性纯白；背景必须均匀纯黑。只输出一张最终线稿图，不输出解释。`
    }
  },
  created() {
    this.loadConfiguration()
    this.loadPendingTask()
  },
  beforeDestroy() {
    this.pollingStopped = true
    if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
    this.requestControllers.forEach(controller => controller.abort())
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
  },
  methods: {
    async request(url, options = {}) {
      return apiRequest(url, options, {
        controllers: this.requestControllers,
        isStopped: () => this.pollingStopped
      })
    },
    async loadConfiguration() {
      try {
        const health = await this.request('/api/health')
        this.apiConfigured = Boolean(health.configured)
      } catch (error) {
        this.apiConfigured = false
      }

      try {
        const prompt = await this.request('/api/prompts/white-model')
        if (prompt.systemPrompt) this.systemPrompt = prompt.systemPrompt
      } catch (error) {
        this.systemPrompt = FALLBACK_SYSTEM_PROMPT
      }
    },
    handleFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (file) this.acceptFile(file)
    },
    handleDrop(event) {
      this.dragging = false
      if (this.generating) return
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (file) this.acceptFile(file)
    },
    acceptFile(file) {
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.showMessage('仅支持 PNG、JPG 或 WebP 效果图', 'warning')
        return
      }
      if (file.size >= 10 * 1024 * 1024) {
        this.showMessage('效果图必须小于 10MB，请压缩后再上传', 'warning')
        return
      }
      const previewUrl = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
        this.setSourceImageDimensions(image.naturalWidth, image.naturalHeight)
        this.floorPlanFile = file
        this.previewUrl = previewUrl
        this.resultUrl = ''
        this.errorMessage = ''
        this.errorSuggestion = ''
        this.errorRetryable = false
        this.currentBatchId = ''
        this.currentTaskIds = []
      }
      image.onerror = () => {
        URL.revokeObjectURL(previewUrl)
        this.showMessage('效果图文件已损坏或无法读取，请重新选择', 'error')
      }
      image.src = previewUrl
    },
    greatestCommonDivisor(a, b) {
      let x = Math.abs(a)
      let y = Math.abs(b)
      while (y) {
        const remainder = x % y
        x = y
        y = remainder
      }
      return x || 1
    },
    setSourceImageDimensions(width, height) {
      if (!width || !height) return
      const divisor = this.greatestCommonDivisor(width, height)
      const exactWidth = width / divisor
      const exactHeight = height / divisor
      const exactLabel = exactWidth <= 100 && exactHeight <= 100
        ? `${exactWidth}:${exactHeight}`
        : `${(width / height).toFixed(2)}:1`
      const orientation = width === height ? 'square' : (width > height ? 'landscape' : 'portrait')
      this.sourceImage = {
        width,
        height,
        ratio: width / height,
        ratioLabel: exactLabel,
        orientation,
        orientationLabel: orientation === 'square' ? '方形' : (orientation === 'landscape' ? '横图' : '竖图')
      }
      this.ratioMode = 'auto'
      this.useDetectedRatio()
    },
    closestSupportedRatio(ratio) {
      return closestImageRatio(ratio, 1, this.ratioOptions)
    },
    useDetectedRatio() {
      if (!this.sourceImage.ratio) return
      this.selectedRatio = this.closestSupportedRatio(this.sourceImage.ratio)
      this.ratioMode = 'auto'
    },
    validateLineSettings() {
      if (this.lineNote.length > 300) {
        throw new Error('补充要求不能超过 300 个字符')
      }
    },
    async uploadPlan() {
      const form = new FormData()
      form.append('file', this.floorPlanFile)
      return this.request('/api/images/upload', { method: 'POST', body: form, timeoutMs: 120000 })
    },
    taskImage(task) {
      const data = task && task.result && task.result.data
      return Array.isArray(data) && data[0] ? data[0].url : ''
    },
    async waitForSubmittedTask(recordId, deadline) {
      this.generationText = '已成功提交到后台，正在等待生成任务';
      this.progress = Math.max(this.progress, 16);
      while (Date.now() < deadline && !this.pollingStopped) {
        try {
          const record = await this.request(`/api/records/${encodeURIComponent(recordId)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '正在确认后台生成任务'
          });
          if (record.status === 'failed') {
            const failedTask = (record.tasks || []).find(item => item && item.status === 'failed');
            const error = new Error((failedTask && failedTask.error && failedTask.error.message) || record.errorMessage || '线稿生成任务失败');
            Object.assign(error, (failedTask && failedTask.error) || {}, { terminal: true });
            throw error;
          }
          const task = (record.tasks || []).find(item => item && item.id);
          if (task) return task.id;
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error;
        }
        const elapsed = 300000 - Math.max(0, deadline - Date.now());
        this.progress = Math.max(this.progress, Math.min(18, 16 + Math.round(elapsed / 150000)));
        await new Promise(resolve => window.setTimeout(resolve, 3000));
      }
      const timeout = new Error('后台生成等待已达到 5 分钟，任务记录仍会保留');
      timeout.code = 'TASK_WAIT_TIMEOUT';
      timeout.suggestion = '可稍后在生成记录中继续查询，无需重复提交。';
      timeout.retryable = true;
      timeout.pending = true;
      throw timeout;
    },
    async pollTask(taskId, deadline = Date.now() + 300000) {
      let queryFailures = 0
      while (Date.now() < deadline && !this.pollingStopped) {
        try {
          const task = await this.request(`/api/generations/${encodeURIComponent(taskId)}`, {
            timeoutMs: 12000,
            retries: 1,
            timeoutMessage: '线稿任务状态查询超时'
          })
          queryFailures = 0
          this.progress = Math.max(this.progress, Math.min(96, Number(task.progress || 0)))
          if (task.status === 'completed') {
            const image = this.taskImage(task)
            if (!image) {
              const error = new Error('任务已完成，但未返回可用的线稿图片')
              error.suggestion = '请联系管理员并提供任务编号，原任务记录已保留。'
              error.retryable = false
              throw error
            }
            return image
          }
          if (task.status === 'failed') {
            const error = new Error(task.error && task.error.message ? task.error.message : 'AI 线稿生成失败')
            Object.assign(error, task.error || {}, { terminal: true })
            throw error
          }
          this.generationText = this.progress > 45 ? `正在校验线稿 ${this.progress}%` : `正在提取线稿 ${this.progress}%`
        } catch (error) {
          if (error.terminal || error.retryable === false) throw error
          queryFailures += 1
          this.generationText = `网络波动，正在恢复线稿进度（${queryFailures}/5）`
          if (queryFailures >= 5) {
            const pending = new Error('暂时无法查询线稿进度，但任务仍可能在后台处理')
            pending.code = 'POLLING_UNAVAILABLE'
            pending.suggestion = '请恢复网络后点击“继续查询”，无需重新上传或创建任务。'
            pending.retryable = true
            pending.pending = true
            throw pending
          }
        }
        await new Promise(resolve => window.setTimeout(resolve, 3000))
      }
      const pending = new Error('线稿生成时间较长，任务仍在后台处理中')
      pending.code = 'TASK_STILL_RUNNING'
      pending.suggestion = '请稍后点击“继续查询”，无需重复创建任务。'
      pending.retryable = true
      pending.pending = true
      throw pending
    },
    async generateWhiteModel() {
      if (this.generating) return
      if (!this.floorPlanFile) {
        this.showMessage('请先上传完整室内效果图', 'warning')
        return
      }
      if (!this.apiConfigured) {
        this.showMessage('请先配置 ToAPIs API Key', 'warning')
        return
      }
      try {
        this.validateLineSettings()
      } catch (error) {
        this.showMessage(error.message, 'warning')
        return
      }

      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.resultUrl = ''
      this.progress = 4
      this.generationText = '正在上传效果图'
      const random = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`
      const requestId = `white-model-${random}`.slice(0, 64)
      const pendingId = `interior-${requestId}`
      const createdAt = new Date().toISOString()
      const generationDeadline = Date.now() + 300000
      this.savePendingTask(pendingId, createdAt)
      let generationRequestStarted = false
      try {
        const asset = await this.uploadPlan()
        this.savePendingTask(pendingId, createdAt, asset.localUrl)
        this.progress = 12
        this.generationText = '正在创建线稿任务'
        generationRequestStarted = true
        const batch = await this.request('/api/generations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            imageUrls: [asset.url],
            prompt: this.finalPrompt,
            space: '原效果图空间',
            style: '黑底白线蜡笔线稿',
            size: this.selectedRatio,
            resolution: this.selectedResolution,
            quality: this.selectedQuality,
            count: 1,
            stage: 'white_model',
            floorPlanRecordUrls: asset.localUrl ? [asset.localUrl] : [],
            referenceRecordUrls: [],
            requestId
          }),
          timeoutMs: 120000,
          retries: 1,
          timeoutMessage: '创建线稿任务等待超时，正在确认是否已成功提交'
        })
        this.currentBatchId = batch.batchId || pendingId
        let taskId = ((batch.tasks || []).find(item => item && item.id) || {}).id
        this.progress = 16
        this.generationText = '已成功提交到后台，预计 5 分钟内完成'
        this.showMessage('任务已成功提交，正在后台生成，请耐心等待', 'success')
        if (!taskId) taskId = await this.waitForSubmittedTask(this.currentBatchId, generationDeadline)
        this.currentTaskIds = [taskId]
        removePendingGeneration(pendingId)
        this.progress = 18
        this.resultUrl = await this.pollTask(taskId, generationDeadline)
        this.progress = 100
        this.generationText = '线稿已生成'
        this.errorSuggestion = ''
        this.showMessage('3D 线稿已生成，请与左侧原图逐项核对', 'success')
      } catch (error) {
        if (generationRequestStarted && !error.terminal && Date.now() < generationDeadline) {
          try {
            this.currentBatchId = this.currentBatchId || pendingId
            const recoveredTaskId = await this.waitForSubmittedTask(this.currentBatchId, generationDeadline)
            this.currentTaskIds = [recoveredTaskId]
            this.resultUrl = await this.pollTask(recoveredTaskId, generationDeadline)
            this.progress = 100
            this.generationText = '线稿已生成'
            this.showMessage('3D 线稿已生成，请与左侧原图逐项核对', 'success')
            return
          } catch (confirmationError) {
            error = confirmationError
          }
        }
        this.errorMessage = error.message || '线稿生成失败，请稍后重试'
        this.errorSuggestion = error.suggestion || '请检查效果图和网络后重新尝试；若任务已创建，请优先继续查询。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (generationRequestStarted && (error.retryable || error.pending)) this.schedulePendingTaskRefresh()
        else if (!this.currentTaskIds.length) removePendingGeneration(pendingId)
        this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    savePendingTask(id, createdAt, localUrl = '') {
      savePendingGeneration({
        id,
        space: '原效果图空间',
        style: '黑底白线蜡笔线稿',
        size: this.selectedRatio,
        prompt: this.finalPrompt,
        status: 'generating',
        stage: 'white_model',
        progress: 0,
        expectedCount: 1,
        tasks: [],
        images: [],
        inputContext: {
          floorPlanImages: localUrl ? [{ type: 'original', label: '原始效果图', url: localUrl }] : [],
          referenceImages: [],
          annotation: null
        },
        createdAt
      })
    },
    async loadPendingTask() {
      if (this.historyLoading) return
      this.historyLoading = true
      this.historyError = ''
      try {
        const response = await this.request(`/api/records/paged?stage=white_model&page=${this.historyPage}&pageSize=12`)
        this.historyTotal = Number(response.total || 0)
        const records = response.items || []
        const merged = (this.historyPage === 1 ? mergePendingGenerations(records, 'white_model') : records)
          .filter(record => record.stage === 'white_model')
        this.history = merged.slice(0, 12).map(record => ({
          id: record.id,
          style: record.style,
          size: record.size,
          prompt: record.prompt || '',
          status: record.status,
          createdAt: record.createdAt,
          source: record.inputContext && record.inputContext.floorPlanImages && record.inputContext.floorPlanImages[0]
            ? record.inputContext.floorPlanImages[0].url
            : '',
          result: record.images && record.images[0] ? (record.images[0].url || record.images[0]) : '',
          imageDimensions: record.images && record.images[0] && record.images[0].width && record.images[0].height
            ? `${record.images[0].width} × ${record.images[0].height}`
            : '',
          taskIds: (record.tasks || []).map(task => task && task.id).filter(Boolean),
          taskStates: record.tasks || [],
          errorMessage: record.error || record.errorMessage || ''
          , imageModel: record.imageModel || ''
        }))
        this.populateHistoryImageDimensions(this.history)
        const active = merged.find(record => record.status === 'generating')
        if (!active) return
        const taskIds = (active.tasks || []).map(task => task.id).filter(Boolean)
        const source = active.inputContext && active.inputContext.floorPlanImages
          ? active.inputContext.floorPlanImages[0]
          : null
        this.currentBatchId = active.id
        this.currentTaskIds = taskIds
        if (!this.previewUrl && source && source.url) this.previewUrl = source.url
        if (taskIds.length) this.continuePolling(true)
        else this.schedulePendingTaskRefresh()
      } catch (error) {
        this.historyError = `${error.message || '暂时无法加载线稿记录'}。请检查网络后重试。`
      } finally {
        this.historyLoading = false
      }
    },
    changeHistoryPage(page) {
      this.historyPage = page
      this.loadPendingTask()
    },
    historyImageDimensions(record) {
      if (record && record.imageDimensions) return record.imageDimensions
      return '无'
    },
    populateHistoryImageDimensions(records) {
      ;(records || []).forEach(record => {
        if (!record) return
      })
    },
    schedulePendingTaskRefresh() {
      if (this.pendingRecordTimer) window.clearTimeout(this.pendingRecordTimer)
      if (this.pollingStopped) return
      this.pendingRecordTimer = window.setTimeout(() => {
        this.pendingRecordTimer = null
        this.loadPendingTask()
      }, 2500)
    },
    async continuePolling(silent = false) {
      if (this.generating || !this.currentTaskIds.length) return
      this.generating = true
      this.errorMessage = ''
      this.errorSuggestion = ''
      this.errorRetryable = false
      this.progress = Math.max(18, this.progress)
      this.generationText = '正在恢复线稿任务进度'
      try {
        this.resultUrl = await this.pollTask(this.currentTaskIds[0])
        this.progress = 100
        this.generationText = '线稿已生成'
        if (!silent) this.showMessage('线稿任务已完成', 'success')
      } catch (error) {
        this.errorMessage = error.message || '查询线稿任务失败'
        this.errorSuggestion = error.suggestion || '请稍后继续查询。'
        this.errorRetryable = Boolean(error.pending || error.retryable)
        if (!silent) this.showMessage(this.errorMessage, error.pending ? 'warning' : 'error')
      } finally {
        this.generating = false
        this.loadPendingTask()
      }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showMessage('提示词已复制', 'success')
      } catch (error) {
        this.showMessage('复制失败，请手动选择提示词', 'warning')
      }
    },
    statusText(status) {
      return {
        generating: '生成中',
        completed: '已完成',
        failed: '生成失败'
      }[status] || '等待处理'
    },
    formatRecordTime(value) {
      if (!value) return '时间未知'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '时间未知'
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(date)
    },
    restoreHistoryRecord(record) {
      if (record.source) {
        if (this.previewUrl && this.previewUrl.startsWith('blob:')) URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = record.source
      }
      this.resultUrl = record.result || ''
      this.currentBatchId = record.id
      this.currentTaskIds = record.taskIds || []
      this.errorMessage = ''
      this.errorSuggestion = ''
      if (record.status === 'generating') {
        if (this.currentTaskIds.length) this.continuePolling(true)
        else this.schedulePendingTaskRefresh()
      }
    },
    previewCurrentImage(type) {
      const image = type === 'result' ? this.resultUrl : this.previewUrl
      if (!image) return
      const index = this.currentViewerItems.findIndex(item => item.src === image)
      this.$emit('preview-image', image, { title: this.currentViewerItems[index].title, items: this.currentViewerItems, index })
    },
    previewHistoryImage(record, type) {
      const items = this.historyViewerItems
      const image = type === 'result' ? record.result : record.source
      if (!image) return
      const key = `${record.id}-${type}`
      const index = items.findIndex(item => item.key === key)
      this.$emit('preview-image', image, {
        title: items[index].title,
        items,
        index
      })
    },
    async downloadHistoryResult(record) {
      try {
        const taskId = Array.isArray(record.taskIds) ? record.taskIds.find(Boolean) : ''
        const filename = `interior-line-art-${Date.now()}`
        if (taskId) {
          downloadGeneratedImageDirectly(taskId, filename)
          this.showMessage('PNG 线稿已开始下载', 'success')
          return
        }
        await downloadImageAsPng(record.result, filename)
        this.showMessage('PNG 线稿已开始下载', 'success')
      } catch (error) {
        this.showMessage(error.message || '下载失败，请稍后重试', 'error')
      }
    },
    async downloadResult() {
      try {
        const filename = `interior-line-art-${Date.now()}`
        const taskId = this.currentTaskIds.find(Boolean)
        if (taskId) {
          downloadGeneratedImageDirectly(taskId, filename)
          this.showMessage('PNG 线稿已开始下载', 'success')
          return
        }
        await downloadImageAsPng(this.resultUrl, filename)
        this.showMessage('PNG 线稿已开始下载', 'success')
      } catch (error) {
        this.showMessage(error.message || '下载失败，请稍后重试', 'error')
      }
    },
    showMessage(message, type = 'info') {
      this.$message({ message, type, showClose: true, duration: 2600, offset: 24 })
    }
  }
}
</script>

<style scoped>
.white-model-workspace { width: calc(100% - 68px); max-width: none; margin: 26px auto 56px; color: #202b25; }
.workspace-kicker { color: #2563eb; font-size: 9px; font-weight: 700; letter-spacing: 2px; }
.method-head h2 { margin: 7px 0 5px; font-size: 19px; letter-spacing: 0; }

.white-model-main { min-height: 700px; display: grid; grid-template-columns: 340px minmax(0, 1fr); border: 1px solid #dde3de; border-radius: 7px; overflow: hidden; background: #fff; box-shadow: 0 12px 34px rgba(35,48,41,.07); }
.white-model-settings { min-width: 0; padding: 22px; border-right: 1px solid #e3e7e4; background: #fafbfa; }
.setting-section + .setting-section { margin-top: 22px; border-top: 1px solid #e5e9e6; padding-top: 20px; }
.section-title { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.section-title > span { width: 26px; height: 26px; flex: 0 0 26px; display: grid; place-items: center; border-radius: 4px; background: #eaf2ff; color: #2563eb; font-size: 9px; font-weight: 700; }
.section-title > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.section-title strong { color: #2d3932; font-size: 12px; }
.section-title small { color: #929a95; font-size: 8px; line-height: 1.45; }
.plan-dropzone { height: 180px; display: grid; place-items: center; position: relative; overflow: hidden; border: 1px dashed #bfc9c2; border-radius: 5px; background: #fff; cursor: pointer; }
.plan-dropzone:hover,.plan-dropzone.dragging { border-color: #2563eb; background: #f1f6ff; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
.plan-dropzone input { display: none; }
.plan-dropzone > img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f1f3f1; }
.dropzone-empty { display: flex; flex-direction: column; align-items: center; gap: 7px; padding: 20px; color: #838d86; text-align: center; }
.dropzone-empty > i { color: #4778d6; font-size: 25px; }
.dropzone-empty strong { color: #46524b; font-size: 11px; }
.dropzone-empty small { font-size: 8px; line-height: 1.5; }
.replace-plan { position: absolute; right: 8px; bottom: 8px; border: 0; border-radius: 3px; padding: 6px 9px; background: rgba(31,43,36,.82); color: #fff; font-size: 9px; }
.file-rule { min-width: 0; display: flex; align-items: center; gap: 6px; margin-top: 8px; color: #929a95; font-size: 8px; }
.file-rule.valid { color: #317e4d; }
.file-rule span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.image-detection { display: flex; justify-content: space-between; gap: 8px; margin-top: 8px; border-radius: 4px; padding: 7px 8px; background: #f3f7ff; color: #63728c; font-size: 8px; }
.image-detection span:first-child { display: inline-flex; gap: 6px; }
.image-detection strong { color: #315da9; }
.dimension-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }
.dimension-grid label, .note-field { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.dimension-grid label > span, .note-field > span { display: flex; justify-content: space-between; color: #667169; font-size: 9px; }
.dimension-grid em, .note-field em { color: #9ca49f; font-size: 8px; font-style: normal; font-weight: 400; }
.dimension-grid input { width: 100%; height: 34px; border: 1px solid #d8ded9; border-radius: 4px; outline: 0; padding: 0 9px; background: #fff; color: #344039; font-size: 10px; }
.dimension-grid input:focus, .note-field textarea:focus { border-color: #6692eb; box-shadow: 0 0 0 3px rgba(37,99,235,.08); }
.note-field { margin-top: 12px; }
.note-field textarea { width: 100%; min-height: 64px; resize: vertical; border: 1px solid #d8ded9; border-radius: 4px; outline: 0; padding: 8px 9px; color: #344039; font-size: 9px; line-height: 1.55; }
.output-section { display: flex; flex-direction: column; gap: 9px; }
.output-section .section-title { margin-bottom: 2px; }
.output-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.output-row > span { color: #68736c; font-size: 9px; }
.output-row .el-select { width: 164px; }
.ratio-match-note { display: flex; align-items: center; gap: 6px; border-radius: 4px; padding: 7px 8px; background: #f3f7ff; color: #63728c; font-size: 8px; line-height: 1.45; }
.ratio-match-note > i { color: #3971d1; font-size: 11px; }
.ratio-match-note > span { flex: 1; }
.ratio-match-note button { flex: 0 0 auto; border: 0; padding: 0; background: transparent; color: #2563eb; font-size: 8px; cursor: pointer; }
.generate-white-model { width: 100%; min-height: 50px; display: grid; grid-template-columns: 24px 1fr; align-items: center; margin-top: 22px; border: 0; border-radius: 5px; padding: 8px 14px; background: #2563eb; color: #fff; box-shadow: 0 7px 17px rgba(37,99,235,.22); }
.generate-white-model:hover { background: #1d4ed8; }
.generate-white-model:disabled { opacity: .72; cursor: wait; }
.generate-white-model > i, .button-spinner { grid-row: 1 / span 2; font-size: 18px; }
.generate-white-model > span:not(.button-spinner) { text-align: left; font-size: 12px; font-weight: 700; }
.generate-white-model small { opacity: .7; text-align: left; font-size: 8px; }
.button-spinner { width: 17px; height: 17px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: whiteSpin .8s linear infinite; }
.generation-progress { height: 3px; margin-top: 7px; overflow: hidden; border-radius: 2px; background: #dce4df; }
.generation-progress i { height: 100%; display: block; background: #2563eb; transition: width .4s ease; }

.comparison-panel { min-width: 0; display: flex; flex-direction: column; padding: 22px; background: #fff; }
.comparison-head { min-height: 45px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.comparison-head > div:first-child { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.comparison-head strong { color: #2d3932; font-size: 13px; }
.comparison-head span { color: #8c958f; font-size: 9px; }
.comparison-actions { display: flex; gap: 6px; }
.comparison-actions button { width: 32px; height: 32px; border: 1px solid #dce2dd; border-radius: 4px; background: #fff; color: #536159; }
.comparison-actions button:hover { border-color: #8aacf0; color: #2563eb; }
.comparison-stage { min-height: 540px; display: grid; grid-template-columns: 1fr 1fr; gap: 1px; margin-top: 13px; overflow: hidden; border: 1px solid #dfe4e0; border-radius: 5px; background: #dfe4e0; }
.compare-viewport.zoomable { cursor: zoom-in; }
.compare-viewport.zoomable:focus-visible { z-index: 2; outline: 3px solid #2563eb; outline-offset: -3px; }
.compare-viewport { min-width: 0; min-height: 0; display: grid; place-items: center; position: relative; overflow: hidden; background: #f3f5f3; }
.compare-viewport > img { width: 100%; height: 100%; display: block; object-fit: contain; background: #f1f3f1; }
.model-viewport { background-color: #edf0ed; background-image: linear-gradient(#e2e6e3 1px, transparent 1px), linear-gradient(90deg, #e2e6e3 1px, transparent 1px); background-size: 24px 24px; }
.viewport-label { position: absolute; z-index: 2; left: 10px; top: 10px; border: 1px solid rgba(214,221,216,.9); border-radius: 3px; padding: 5px 7px; background: rgba(255,255,255,.9); color: #516058; font-size: 8px; font-weight: 700; }
.viewport-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 28px; color: #919a94; text-align: center; }
.viewport-empty > i { color: #7890bc; font-size: 29px; }
.viewport-empty strong { color: #536058; font-size: 11px; }
.viewport-empty > span:last-child { max-width: 250px; font-size: 8px; line-height: 1.6; }
.line-art-empty-icon { font-size: 52px; color: #eef3ef; text-shadow: 0 0 18px rgba(255,255,255,.22); }
.wireframe-cube { width: 84px; height: 70px; position: relative; margin-bottom: 7px; transform: rotateX(58deg) rotateZ(45deg); }
.wireframe-cube i { position: absolute; border: 2px solid #7f9fdc; background: rgba(255,255,255,.55); }
.wireframe-cube i:nth-child(1) { inset: 5px 5px 24px 5px; }
.wireframe-cube i:nth-child(2) { width: 28px; height: 32px; left: 5px; bottom: 2px; }
.wireframe-cube i:nth-child(3) { width: 36px; height: 25px; right: 5px; bottom: 2px; }
.wireframe-cube i:nth-child(4) { width: 25px; height: 18px; right: 5px; top: 5px; }
.model-loader { width: 72px; height: 60px; position: relative; margin-bottom: 10px; }
.model-loader i { position: absolute; width: 43px; height: 43px; left: 14px; border: 2px solid #6f94db; transform: rotate(30deg) skew(-8deg); animation: modelPulse 1.5s ease-in-out infinite; }
.model-loader i:nth-child(2) { top: 8px; opacity: .65; animation-delay: .18s; }
.model-loader i:nth-child(3) { top: 16px; opacity: .35; animation-delay: .36s; }
.accuracy-strip { min-height: 48px; display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 7px 16px; border: 1px solid #dfe5e0; border-top: 0; border-radius: 0 0 5px 5px; background: #fafbfa; }
.accuracy-strip span { display: inline-flex; align-items: center; gap: 4px; color: #647068; font-size: 8px; }
.accuracy-strip i { color: #338753; }
.generation-error { display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; border-left: 3px solid #d54a4a; padding: 10px 12px; background: #fff5f5; color: #a13f3f; }
.generation-error > i { margin-top: 1px; }
.generation-error > div { display: flex; flex-direction: column; gap: 3px; }
.generation-error strong { font-size: 10px; }
.generation-error span { font-size: 8px; line-height: 1.55; }

.ai-method-panel { margin-top: 22px; border: 1px solid #dde3de; border-radius: 7px; background: #fff; box-shadow: 0 10px 28px rgba(35,48,41,.05); }
.method-head { min-height: 78px; display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 16px 22px; border-bottom: 1px solid #e6eae7; }
.method-head h2 { font-size: 16px; }
.prompt-toggle { display: inline-flex; align-items: center; gap: 6px; border: 1px solid #c8d7f5; border-radius: 4px; padding: 8px 11px; background: #f4f7fd; color: #245cc5; font-size: 9px; }
.method-steps { display: grid; grid-template-columns: repeat(4, 1fr); border-bottom: 1px solid #e6eae7; }
.method-steps article { min-width: 0; min-height: 120px; display: grid; grid-template-columns: 25px 32px 1fr; align-items: flex-start; gap: 8px; padding: 22px 18px; }
.method-steps article + article { border-left: 1px solid #e6eae7; }
.method-steps article > span { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 50%; background: #eaf2ff; color: #2563eb; font-size: 8px; font-weight: 700; }
.method-steps article > i { color: #4f76bd; font-size: 22px; text-align: center; }
.method-steps article > div { min-width: 0; }
.method-steps strong { display: block; color: #344139; font-size: 10px; }
.method-steps p { margin: 5px 0 0; color: #89928c; font-size: 8px; line-height: 1.6; }
.prompt-content { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; padding: 20px 22px 22px; background: #fafbfa; }
.prompt-block { min-width: 0; border: 1px solid #dfe4e0; border-radius: 5px; overflow: hidden; background: #fff; }
.prompt-block header { min-height: 53px; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border-bottom: 1px solid #e8ebe9; }
.prompt-block header > div { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.prompt-block header span { color: #6f91d4; font-size: 7px; font-weight: 700; letter-spacing: 1.4px; }
.prompt-block header strong { color: #3b4740; font-size: 10px; }
.prompt-block header button { width: 30px; height: 30px; flex: 0 0 30px; border: 1px solid #dfe4e0; border-radius: 4px; background: #fff; color: #647169; }
.prompt-block header button:hover { border-color: #8baaf0; color: #2563eb; }
.prompt-block pre { height: 310px; margin: 0; overflow: auto; padding: 14px; color: #526059; font-family: "Microsoft YaHei", sans-serif; font-size: 9px; line-height: 1.75; white-space: pre-wrap; word-break: break-word; }
.negative-constraints { grid-column: 1 / -1; display: flex; align-items: center; flex-wrap: wrap; gap: 7px; border-left: 3px solid #d34b4b; padding: 10px 12px; background: #fff7f7; }
.negative-constraints strong { margin-right: 4px; color: #944040; font-size: 9px; }
.negative-constraints span { border: 1px solid #ebcece; border-radius: 3px; padding: 4px 7px; background: #fff; color: #8e5d5d; font-size: 8px; }
.prompt-reveal-enter-active, .prompt-reveal-leave-active { transition: opacity .18s ease; }
.prompt-reveal-enter, .prompt-reveal-leave-to { opacity: 0; }

.white-model-history { margin-top: 22px; border: 1px solid #dde3de; border-radius: 7px; padding: 22px; background: #fff; box-shadow: 0 10px 28px rgba(35,48,41,.05); }
.history-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; margin-bottom: 17px; }
.history-head h2 { margin: 7px 0 5px; color: #26322b; font-size: 16px; }
.history-head p { margin: 0; color: #87918b; font-size: 9px; line-height: 1.55; }
.history-refresh { min-height: 44px; display: inline-flex; align-items: center; gap: 7px; flex: 0 0 auto; border: 1px solid #c8d7f5; border-radius: 5px; padding: 0 13px; background: #f4f7fd; color: #245cc5; font-size: 10px; cursor: pointer; }
.history-refresh:disabled { opacity: .65; cursor: wait; }
.white-model-history-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.white-model-history-grid article { min-width: 0; overflow: hidden; border: 1px solid #dfe4e0; border-radius: 7px; background: #fff; transition: border-color .18s ease, box-shadow .18s ease; }
.white-model-history-grid article:hover, .white-model-history-grid article.active { border-color: #91afea; box-shadow: 0 10px 24px rgba(37,99,235,.1); }
.history-preview { width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 1px; position: relative; background: #e5e9e6; }
.history-image { min-width: 0; aspect-ratio: 16 / 10; display: grid; place-items: center; position: relative; overflow: hidden; border: 0; padding: 0; background: #f3f5f3; }
button.history-image { cursor: zoom-in; }
button.history-image:focus-visible { z-index: 3; outline: 3px solid #2563eb; outline-offset: -3px; }
.history-image.result { background: #161b18; }
.history-image img { width: 100%; height: 100%; display: block; object-fit: contain; transition: filter .18s ease, transform .18s ease; }
button.history-image:hover img { filter: brightness(.9); transform: scale(1.02); }
.history-zoom-icon { width: 30px; height: 30px; position: absolute; right: 8px; bottom: 8px; display: grid; place-items: center; border-radius: 50%; background: rgba(20,28,23,.78); color: #fff; font-size: 13px; opacity: 0; transform: translateY(4px); transition: opacity .18s ease, transform .18s ease; }
button.history-image:hover .history-zoom-icon, button.history-image:focus-visible .history-zoom-icon { opacity: 1; transform: translateY(0); }
.history-image > span { display: flex; flex-direction: column; align-items: center; gap: 7px; color: #89928c; font-size: 9px; }
.history-image.result > span { color: #aeb9b2; }
.history-image > span i { font-size: 22px; }
.history-image small { position: absolute; left: 8px; bottom: 8px; border-radius: 3px; padding: 4px 6px; background: rgba(27,39,32,.78); color: #fff; font-size: 8px; }
.history-image.result small { background: rgba(37,99,235,.88); }
.history-status { position: absolute; z-index: 2; right: 8px; top: 8px; border-radius: 10px; padding: 4px 7px; background: #eef1ef; color: #69746d; font-size: 8px; }
.history-status.completed { background: #e8f6ed; color: #277447; }
.history-status.generating { background: #edf3ff; color: #245cc5; }
.history-status.failed { background: #fff0f0; color: #b74444; }
.white-model-history-grid footer { min-height: 88px; display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 11px 12px; }
.white-model-history-grid footer > div { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.white-model-history-grid footer strong { overflow: hidden; color: #2d3932; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.history-record-details { display: grid; gap: 3px; margin: 1px 0 0; }
.history-record-details > div { min-width: 0; display: grid; grid-template-columns: 46px minmax(0, 1fr); gap: 7px; align-items: baseline; }
.history-record-details dt { color: #89928c; font-size: 8px; }
.history-record-details dd { min-width: 0; margin: 0; overflow: hidden; color: #5f6b64; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
.history-record-actions { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.white-model-history-grid footer .history-record-actions button { width: 44px; height: 44px; flex: 0 0 44px; border: 1px solid #dce2dd; border-radius: 5px; background: #fff; color: #536159; cursor: pointer; }
.white-model-history-grid footer .history-record-actions button:hover { border-color: #8aacf0; color: #2563eb; }
.white-model-history-grid footer .history-record-actions button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }
.history-empty { min-height: 180px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; border: 1px dashed #d4dad6; border-radius: 6px; background: #fafbfa; color: #8b958f; text-align: center; }
.history-empty > i { color: #7890bc; font-size: 30px; }
.history-empty strong { color: #536058; font-size: 11px; }
.history-empty span { max-width: 420px; font-size: 8px; line-height: 1.6; }

@keyframes whiteSpin { to { transform: rotate(360deg); } }
@keyframes modelPulse { 0%, 100% { transform: rotate(30deg) skew(-8deg) scale(.96); opacity: .35; } 50% { transform: rotate(30deg) skew(-8deg) scale(1.04); opacity: 1; } }

@media (max-width: 1120px) {
  .white-model-main { grid-template-columns: 310px minmax(0, 1fr); }
  .comparison-stage { min-height: 460px; }
  .method-steps { grid-template-columns: repeat(2, 1fr); }
  .method-steps article:nth-child(3) { border-top: 1px solid #e6eae7; border-left: 0; }
  .method-steps article:nth-child(4) { border-top: 1px solid #e6eae7; }
  .white-model-history-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 820px) {
  .white-model-workspace { width: calc(100% - 24px); margin-top: 16px; }
  .white-model-main { grid-template-columns: 1fr; }
  .white-model-settings { border-right: 0; border-bottom: 1px solid #e3e7e4; }
  .comparison-stage { min-height: 720px; grid-template-columns: 1fr; grid-template-rows: 1fr 1fr; }
  .prompt-content { grid-template-columns: 1fr; }
  .prompt-block pre { height: 280px; }
  .history-head { flex-direction: column; }
  .history-refresh { width: 100%; justify-content: center; }
}

.history-image-dimension { min-height:38px; display:flex; align-items:center; gap:7px; border-top:1px solid #e2e7e3; border-bottom:1px solid #e7ebe8; padding:0 11px; background:#f8faf9; color:#7b8780; font-size:9px; }
.history-image-dimension > i { color:#5076b9; font-size:12px; }
.history-image-dimension > strong { margin-left:auto; color:#34443b; font-size:10px; font-variant-numeric:tabular-nums; }
@media (max-width: 560px) {
  .method-head { align-items: flex-start; flex-direction: column; }
  .method-steps { grid-template-columns: 1fr; }
  .method-steps article + article { border-top: 1px solid #e6eae7; border-left: 0; }
  .comparison-panel { padding: 14px; }
  .comparison-stage { min-height: 620px; }
  .accuracy-strip { align-items: flex-start; flex-direction: column; padding: 12px; }
  .white-model-history { padding: 14px; }
  .white-model-history-grid { grid-template-columns: 1fr; }
}
</style>
